import React, {CSSProperties, FC} from "react"
import {extend} from "lodash"
import SimpleMarkdown from "simple-markdown"
import {Body} from "@irati/ui/src/Typography"

import theme from "@irati/ui/src/global/theme"

import Expander from "../Expander"
import {StyledExpanderIcon, HeaderLabel} from "./styled"

const paragraphStyles: CSSProperties = {
  color: theme.colors.utility.contrast,
  fontSize: 16,
  letterSpacing: 0.3,
  marginBottom: theme.spacing.oneAndHalf
}

const anchorStyles: CSSProperties = {
  color: theme.colors.primary1,
  fontFamily: theme.font.bold
}

const paragraphRule = extend({}, SimpleMarkdown.defaultRules.paragraph, {
  react: (node, output, state) => {
    return (
      <Body key={state.key}>
        {output(node.content)}
      </Body>
    )
  }
})

const anchorRule = extend({}, SimpleMarkdown.defaultRules.link, {
  react: (node, output, state) => {
    const props = {
      key: state.key,
      href: SimpleMarkdown.sanitizeUrl(node.target),
      title: node.title,
      target: "_blank",
      style: anchorStyles
    }
    return <a {...props}>{output(node.content)}</a>
  }
})

const strongRule = extend({}, SimpleMarkdown.defaultRules.strong, {
  react: (node, output, state) => {
    return (
      <span key={state.key} style={{...paragraphStyles, fontFamily: theme.font.bold}}>
        {output(node.content, state)}
      </span>
    )
  }
})

const expandRule = {
  order: SimpleMarkdown.defaultRules.codeBlock.order + 0.5,
  match: (source) => /^\|:(.+)\n([\s\S]+?):\|/.exec(source),
  parse: (capture, parse, state) => {
    return {
      buttonText: capture[1],
      content: parse(`${capture[2]}\n\n`, state)
    }
  },
  react: (node, output, state) => {
    return (
      <div key={state.key}>
        <Expander showText={node.buttonText} hideText={node.buttonText} Arrow={StyledExpanderIcon} HeaderLabel={HeaderLabel}>
          {output(node.content, {inline: false})}
        </Expander>
      </div>
    )
  }
}

const rules = extend({}, SimpleMarkdown.defaultRules, {
  link: anchorRule,
  strong: strongRule,
  expand: expandRule,
  paragraph: paragraphRule
})
const parser = SimpleMarkdown.parserFor(rules)
const reactOutput = SimpleMarkdown.reactFor(SimpleMarkdown.ruleOutput(rules, "react"))

interface Props {
  source?: string,
  className?: string
}

const Markdown: FC<Props> = (props) => {
  // @notes: Many rules require content to end in \n\n to be interpreted as a block. (notes from simple-markdown)
  const blockSource = `${props.source || ""}\n\n`
  const parseTree = parser(blockSource, {inline: false})
  return <div className={props.className}>{reactOutput(parseTree)}</div>
}

export default Markdown
